/* ══════════════════════════════════════════════════════════════════════════
   HAVANI — CART

   The drawer and the cart page are the same section rendered at two scales.
   Neither keeps state: every quantity change goes to /cart/change.js and the
   response re-renders the markup Liquid produced, so totals, discounts and
   the free-shipping bar can never drift from what Shopify actually thinks.

   The cart page also works with JavaScript disabled — the quantity inputs
   and the remove links sit inside a real <form action="/cart" method="post">
   with an update button, and checkout is a plain submit.
   ══════════════════════════════════════════════════════════════════════════ */

(function () {
  'use strict';
  const $ = (s, r) => (r || document).querySelector(s);
  const $$ = (s, r) => Array.from((r || document).querySelectorAll(s));

  class HvCartItems extends HTMLElement {
    connectedCallback() {
      this.addEventListener('change', (e) => {
        const input = e.target.closest('[data-quantity-input]');
        if (input) this.change(input.dataset.lineKey, Number(input.value), input);
      });
      this.addEventListener('click', (e) => {
        const remove = e.target.closest('[data-remove-line]');
        if (remove) {
          e.preventDefault();
          this.change(remove.dataset.lineKey, 0, remove);
        }
      });
    }

    async change(key, quantity, source) {
      if (!key) return;
      this.classList.add('is-busy');
      source?.closest('.line-item')?.classList.add('is-updating');
      try {
        const data = await window.HV.cart.change({ id: key, quantity });
        if (quantity === 0) {
          window.HV.toast(window.hvStrings?.removed || 'Removed');
        }
        /* Shopify caps the line at available stock rather than erroring */
        const line = (data.items || []).find((i) => i.key === key);
        if (line && quantity > 0 && line.quantity < quantity) {
          window.HV.toast(
            (window.hvStrings?.stockLimit || 'Only [n] left').replace('[n]', line.quantity),
            true
          );
        }
      } catch (err) {
        window.HV.toast(err.message, true);
      } finally {
        this.classList.remove('is-busy');
      }
    }
  }
  if (!customElements.get('hv-cart-items')) customElements.define('hv-cart-items', HvCartItems);

  /* order note, debounced so it does not post on every keystroke */
  class HvCartNote extends HTMLElement {
    connectedCallback() {
      const field = $('textarea', this);
      if (!field) return;
      field.addEventListener('input', () => {
        clearTimeout(this.timer);
        this.timer = setTimeout(() => {
          window.HV.cart.update({ note: field.value }).catch(() => {});
        }, 700);
      });
    }
  }
  if (!customElements.get('hv-cart-note')) customElements.define('hv-cart-note', HvCartNote);

  /* Discount code entry.
     Shopify has no storefront API for applying a code to the cart, so the
     honest implementation is to carry it to checkout on the /discount route,
     which is exactly what Shopify supports. Nothing here pretends to validate
     a code the theme cannot see. */
  class HvDiscountForm extends HTMLElement {
    connectedCallback() {
      const form = $('form', this);
      if (!form) return;
      form.addEventListener('submit', (e) => {
        const input = $('input[name="discount"]', form);
        const code = (input?.value || '').trim();
        if (!code) { e.preventDefault(); return; }
        form.action = `/discount/${encodeURIComponent(code)}`;
      });
    }
  }
  if (!customElements.get('hv-discount-form')) customElements.define('hv-discount-form', HvDiscountForm);

  /* CART UPSELL — "Pairs well with", three pieces under the lines in the
     drawer. Complementary products first (Search & Discovery), related as the
     fallback. Re-run after every cart change because the drawer body is
     re-rendered from the server. */
  const loadUpsell = async () => {
    const host = $('[data-cart-upsell]');
    if (!host || !host.dataset.productId || host.dataset.loaded) return;
    host.dataset.loaded = '1';
    const base = host.dataset.url || '/recommendations/products';
    const get = async (intent) => {
      const res = await fetch(`${base}?section_id=cart-upsell&limit=6&intent=${intent}&product_id=${host.dataset.productId}`);
      if (!res.ok) return null;
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      return doc.querySelector('[data-upsell-ready]');
    };
    try {
      const block = (await get('complementary')) || (await get('related'));
      if (block) host.replaceChildren(block);
    } catch (e) { /* the upsell is optional */ }
  };
  document.addEventListener('DOMContentLoaded', loadUpsell);
  if (document.readyState !== 'loading') loadUpsell();
  document.addEventListener('hv:cart:updated', () => setTimeout(loadUpsell, 0));
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-upsell-add]');
    if (!btn || !window.HV || !window.HV.cart) return;
    e.preventDefault();
    btn.disabled = true;
    try {
      await window.HV.cart.add(Number(btn.dataset.upsellAdd), 1);
      window.HV.toast(window.hvStrings?.added || 'Added to bag');
    } catch (err) {
      window.HV.toast(err.message, true);
      btn.disabled = false;
    }
  });
})();
